import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Trash2,
  Plus,
  FileImage,
  Download,
  GripVertical,
} from "lucide-react";
import { useAuthedImage } from "@/lib/useAuthedImage";
import { downloadAuthedFile } from "@/lib/downloadAuthedFile";
import { useDraggableReorder } from "@/lib/useDraggableReorder";
import { toast } from "sonner";

/**
 * Side-by-side "Compare with GA" review screen.
 *
 * Why this exists: AI extraction from a General Arrangement drawing has a
 * non-zero error rate (e.g. "10 knots" read as "9", or the engine row
 * silently dropped when the GA text is small). The flyer pipeline quotes
 * Principal Particulars verbatim to clients, so a single misread can
 * surface in a contract negotiation. This component puts the GA next to
 * the editable PP + KF table so the admin can scan line-by-line.
 *
 * The GA image is pannable (drag) and zoomable (wheel + buttons). All
 * form edits bind directly to the parent's ``form`` / ``setForm`` so
 * changes persist when the modal closes — there is no separate "Save in
 * modal" step.
 */
export default function CompareWithGA({ open, onClose, form, setForm, vesselName }) {
  // Prefer the rendered PNG preview when available (already converted from
  // PDF, much faster to load). Fall back to the original ga_path otherwise.
  const gaPath = form?.ga_preview_path || form?.ga_path || null;
  const { src: gaSrc, loading: gaLoading } = useAuthedImage(open ? gaPath : null);

  // Pan/zoom state. Scale is clamped to a sensible range so the admin
  // can't lose the image off-screen with a runaway scroll.
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const containerRef = useRef(null);

  // Reset zoom/pan whenever the modal is reopened so a new vessel always
  // starts at "fit to screen" instead of the previous vessel's deep-zoom.
  useEffect(() => {
    if (open) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open]);

  const clampScale = (s) => Math.max(0.5, Math.min(8, s));

  const onWheel = (e) => {
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    setScale((s) => clampScale(s * direction));
  };

  const onMouseDown = (e) => {
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  };

  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    setOffset({
      x: dragRef.current.baseX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.baseY + (e.clientY - dragRef.current.startY),
    });
  };

  const stopDrag = () => {
    dragRef.current.active = false;
  };

  const resetView = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  const updateSpec = (i, patch) => {
    setForm((f) => {
      const arr = [...(f.principal_particulars || [])];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, principal_particulars: arr };
    });
  };

  const removeSpec = (i) => {
    setForm((f) => ({
      ...f,
      principal_particulars: (f.principal_particulars || []).filter(
        (_, x) => x !== i
      ),
    }));
  };

  const addSpec = () => {
    setForm((f) => ({
      ...f,
      principal_particulars: [
        ...(f.principal_particulars || []),
        { label: "", value: "", enabled: true },
      ],
    }));
  };

  const updateFeature = (i, patch) => {
    setForm((f) => {
      const arr = [...(f.key_features || [])];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, key_features: arr };
    });
  };

  // Compare-with-GA prefers the rendered PNG preview for display, but the
  // download must always grab the ORIGINAL upload (typically a PDF) so the
  // admin gets the source file the way they put it in.
  const downloadGa = async () => {
    const target = form?.ga_path || form?.ga_preview_path;
    if (!target) return;
    try {
      await downloadAuthedFile(target);
    } catch (e) {
      toast.error(e?.message || "Could not download GA");
    }
  };
  const downloadRender = async () => {
    if (!form?.render_path) return;
    try {
      await downloadAuthedFile(form.render_path);
    } catch (e) {
      toast.error(e?.message || "Could not download render");
    }
  };

  // Drag-to-reorder the PP list inside the compare modal — mirrors the
  // editor behaviour so the admin can rearrange while cross-checking
  // against the GA without bouncing back out.
  const ppReorder = useDraggableReorder(
    form?.principal_particulars || [],
    (next) => setForm((f) => ({ ...f, principal_particulars: next })),
  );

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent
        className="bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] max-w-[95vw] w-[95vw] h-[92vh] p-0 flex flex-col"
        data-testid="compare-with-ga-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-[#DDE5EC] shrink-0">
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <FileImage className="h-4 w-4 text-[#0088CC]" />
            Compare against GA — {vesselName || "vessel"}
            <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#5B6B7C] font-normal">
              Drag to pan · Scroll to zoom
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-0">
          {/* LEFT — GA drawing with pan/zoom */}
          <div className="relative flex flex-col bg-white border-r border-[#DDE5EC] min-h-0">
            <div
              ref={containerRef}
              className="relative flex-1 overflow-hidden cursor-grab active:cursor-grabbing"
              onWheel={onWheel}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={stopDrag}
              onMouseLeave={stopDrag}
              data-testid="compare-ga-viewport"
            >
              {!gaPath && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-[#5B6B7C]">
                  No GA uploaded yet. Upload one in the editor to compare.
                </div>
              )}
              {gaPath && gaLoading && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-[#5B6B7C]">
                  Loading GA…
                </div>
              )}
              {gaSrc && (
                <img
                  src={gaSrc}
                  alt="General Arrangement drawing"
                  draggable={false}
                  className="absolute left-1/2 top-1/2 select-none pointer-events-none"
                  style={{
                    transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                    transformOrigin: "center center",
                    maxWidth: "none",
                    maxHeight: "none",
                  }}
                  data-testid="compare-ga-image"
                />
              )}
            </div>
            <div className="flex items-center gap-2 px-3 py-2 border-t border-[#DDE5EC] bg-white shrink-0">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setScale((s) => clampScale(s * 1.25))}
                className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                data-testid="compare-zoom-in"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setScale((s) => clampScale(s / 1.25))}
                className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                data-testid="compare-zoom-out"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={resetView}
                className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                data-testid="compare-zoom-reset"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Fit
              </Button>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#5B6B7C] font-display tabular-nums">
                {Math.round(scale * 100)}%
              </span>
              {(form?.ga_path || form?.ga_preview_path) && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={downloadGa}
                  className="border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50"
                  data-testid="compare-download-ga"
                  title="Download the original GA file"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> GA
                </Button>
              )}
              {form?.render_path && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={downloadRender}
                  className="border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50"
                  data-testid="compare-download-render"
                  title="Download the render"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Render
                </Button>
              )}
            </div>
          </div>

          {/* RIGHT — editable PP + KF list bound directly to the form */}
          <div className="overflow-y-auto p-4 space-y-5">
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.25em] text-[#0088CC]">
                  Principal Particulars
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addSpec}
                  className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                  data-testid="compare-add-particular"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add
                </Button>
              </div>
              <p className="text-[11px] text-[#5B6B7C] mb-3">
                Cross-check each value against the GA on the left. Untick rows
                you don&apos;t want on the flyer.
              </p>
              <div className="space-y-1.5">
                {(form?.principal_particulars || []).length === 0 && (
                  <div className="text-xs text-[#5B6B7C] italic">
                    No particulars yet — add rows here as you read them off
                    the GA.
                  </div>
                )}
                {(form?.principal_particulars || []).map((sp, i) => {
                  const enabled = sp.enabled !== false;
                  // Highlight obvious AI-guess placeholders so the admin
                  // can spot them at a glance against the GA.
                  const isUnknown = (sp.value || "").trim().toLowerCase() === "unknown";
                  const isDragging = ppReorder.draggingIndex === i;
                  const isOver = ppReorder.overIndex === i && ppReorder.draggingIndex !== null && ppReorder.draggingIndex !== i;
                  return (
                    <div
                      key={i}
                      {...ppReorder.rowProps(i)}
                      className={`grid grid-cols-12 gap-2 items-center rounded border p-2 ${
                        isUnknown
                          ? "border-amber-400/40 bg-amber-500/[0.06]"
                          : "border-[#DDE5EC] bg-white"
                      } ${enabled ? "" : "opacity-60"} ${isDragging ? "opacity-40" : ""} ${
                        isOver ? "ring-2 ring-[#0088CC]/60 border-[#0088CC]/40" : ""
                      }`}
                      data-testid={`compare-particular-${i}`}
                    >
                      <div className="col-span-1 flex justify-center">
                        <button
                          type="button"
                          {...ppReorder.handleProps(i)}
                          className="p-1 text-[#5B6B7C] hover:text-[#0088CC] cursor-grab active:cursor-grabbing"
                          title="Drag to reorder"
                          data-testid={`compare-particular-drag-${i}`}
                        >
                          <GripVertical className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <Checkbox
                          checked={enabled}
                          onCheckedChange={(v) => updateSpec(i, { enabled: !!v })}
                          className="border-[#C4D0DB] data-[state=checked]:bg-[#0088CC] data-[state=checked]:border-[#0088CC]"
                          aria-label={`Include ${sp.label || "row"} on the flyer`}
                        />
                      </div>
                      <div className="col-span-4">
                        <Input
                          value={sp.label}
                          onChange={(e) => updateSpec(i, { label: e.target.value })}
                          placeholder="Length Overall"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-sm focus-visible:ring-[#0088CC]"
                        />
                      </div>
                      <div className="col-span-5">
                        <Input
                          value={sp.value}
                          onChange={(e) => updateSpec(i, { value: e.target.value })}
                          placeholder="35.00 m"
                          className={`bg-white h-8 text-sm focus-visible:ring-[#0088CC] ${
                            isUnknown
                              ? "border-amber-400/50 text-amber-200"
                              : "border-[#DDE5EC] text-[#011836]"
                          }`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeSpec(i)}
                          className="p-1 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                          aria-label="Remove particular"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="border-t border-[#DDE5EC] pt-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#0088CC] mb-2">
                Key Features
              </div>
              <p className="text-[11px] text-[#5B6B7C] mb-3">
                Verify each feature describes something actually visible on
                the GA — common AI misses are claiming twin engines on a
                single-engine drawing, or fore/aft symmetry that only
                exists on one end.
              </p>
              <div className="space-y-1.5">
                {(form?.key_features || []).map((kf, i) => (
                  <div
                    key={i}
                    className="rounded border border-[#DDE5EC] bg-white p-2 space-y-1.5"
                    data-testid={`compare-feature-${i}`}
                  >
                    <Input
                      value={kf.title}
                      onChange={(e) => updateFeature(i, { title: e.target.value })}
                      placeholder="Title"
                      className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-sm font-display tracking-wide focus-visible:ring-[#0088CC]"
                    />
                    <Input
                      value={kf.description}
                      onChange={(e) => updateFeature(i, { description: e.target.value })}
                      placeholder="Short description"
                      className="bg-white border-[#DDE5EC] text-[#011836] h-8 text-sm focus-visible:ring-[#0088CC]"
                    />
                  </div>
                ))}
                {(form?.key_features || []).length === 0 && (
                  <div className="text-xs text-[#5B6B7C] italic">
                    No key features yet.
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#DDE5EC] flex items-center justify-between shrink-0 bg-white">
          <span className="text-xs text-[#5B6B7C]">
            Edits save automatically to the vessel form — close when you&apos;re done.
          </span>
          <Button
            type="button"
            onClick={onClose}
            className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
            data-testid="compare-with-ga-done"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
